import {useState, type FormEvent} from 'react'
import {useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {useQueryClient} from '@tanstack/react-query'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Tag} from '@/components/ui/tag'
import {FormDialogFooter} from '@/components/form-dialog-footer'
import {FieldError} from '@/components/field-error'
import {HelpIcon} from '@/components/help-icon'
import {Hint} from '@/components/ui/hint'
import {InviteLink} from '@/components/invite-link'
import {CiiuPicker, RiskOptionsPicker} from '@/components/ciiu-risk-picker'
import {useAdminUserLookup} from '@/hooks/empresa/organizacion/use-businesses'
import {ConfirmarCuentaDeInciDialog} from '@/components/confirmar-identidad-dialog'
import {pideConfirmarIdentidad} from '@/hooks/platform/use-access'
import {useDebouncedValue} from '@/hooks/common/use-debounced-value'
import {type CiiuCode} from '@/hooks/common/referencias/use-ciiu'
import {api} from '@/lib/api'
import {translateApiError} from '@/lib/errors'
import {DOCUMENTO_MAX, type TipoDocumento} from '@/lib/documento'
import {TipoDocumentoSelect} from '@/components/tipo-documento-select'
import {validateCedula, validateEmail, validateNit, validatePhone, validateWorkerCount} from '@/lib/validation'
import type {components} from '@/lib/api-schema'
import {tope} from '@/lib/campos-texto'

type BusinessCreated = components['schemas']['BusinessCreateRead']
type BillingCycle = components['schemas']['BillingCycle']

type FormState = {
  nit: string
  name: string
  legal_rep_cedula: string
  legal_rep_tipo_documento: TipoDocumento
  legal_rep_name: string
  legal_rep_last_name: string
  legal_rep_email: string
  legal_rep_phone: string
  declared_worker_count: string
  billing_cycle: BillingCycle
}

type FieldErrors = Partial<Record<keyof FormState | 'ciiu_code' | 'risk_classification_id', string>>

const emptyForm: FormState = {
  nit: '',
  name: '',
  legal_rep_cedula: '',
  legal_rep_tipo_documento: 'CC',
  legal_rep_name: '',
  legal_rep_last_name: '',
  legal_rep_email: '',
  legal_rep_phone: '',
  declared_worker_count: '',
  billing_cycle: 'mensual',
}

// Identity fields are only required when the cédula is a brand-new person; an existing one is reused.
function validateForm(form: FormState, repIsNew: boolean, ciiu: CiiuCode | null, riskId: string): FieldErrors {
  const errors: FieldErrors = {}
  // Ninguno se deriva del otro: un mismo CIIU admite varias clases de riesgo según la actividad.
  if (!ciiu) errors.ciiu_code = 'errors.required'
  else if (!riskId) errors.risk_classification_id = 'errors.required'
  const nit = validateNit(form.nit)
  if (nit) errors.nit = nit
  const cedula = validateCedula(form.legal_rep_cedula, form.legal_rep_tipo_documento)
  if (cedula) errors.legal_rep_cedula = cedula
  // Siempre obligatorio: el backend no tiene default para este campo a propósito.
  const workers = validateWorkerCount(form.declared_worker_count)
  if (workers) errors.declared_worker_count = workers
  if (repIsNew) {
    // The invite is emailed to the legal rep, so an email is required to create one.
    const email = validateEmail(form.legal_rep_email)
    if (email) errors.legal_rep_email = email
    const phone = form.legal_rep_phone ? validatePhone(form.legal_rep_phone) : null
    if (phone) errors.legal_rep_phone = phone
  }
  return errors
}

export function CreateBusinessDialog({trigger}: {trigger: React.ReactNode}) {
  const {t} = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [created, setCreated] = useState<BusinessCreated | null>(null)
  const [confirmando, setConfirmando] = useState(false)
  // Ref de callback: Radix monta el nodo después del primer efecto y `useRef` daría `null`.
  const [contenedor, setContenedor] = useState<HTMLElement | null>(null)
  const [ciiu, setCiiu] = useState<CiiuCode | null>(null)
  const [riskId, setRiskId] = useState('')

  const debouncedCedula = useDebouncedValue(form.legal_rep_cedula, 400)
  const {data: lookup, isFetching: lookingUp} = useAdminUserLookup(debouncedCedula)
  // Only trust a lookup whose key matches the settled (debounced) cédula, or a fast edit shows
  // the previous result.
  const cedulaValid = validateCedula(form.legal_rep_cedula, form.legal_rep_tipo_documento) === null
  const lookupSettled = cedulaValid && debouncedCedula === form.legal_rep_cedula && !lookingUp
  const repExists = lookupSettled && lookup?.existe === true
  const repIsNew = lookupSettled && lookup?.existe === false

  function set(field: keyof FormState) {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm((f) => ({...f, [field]: e.target.value}))
      setFieldErrors((fe) => ({...fe, [field]: undefined}))
    }
  }

  // La clase elegida es una fila del Decreto 768 del CIIU anterior; el PATCH de empresa también la limpia.
  function handleCiiuChange(next: CiiuCode | null) {
    setCiiu(next)
    setRiskId('')
    setFieldErrors((fe) => ({...fe, ciiu_code: undefined, risk_classification_id: undefined}))
  }

  function handleRiskPick(id: string) {
    setRiskId(id)
    setFieldErrors((fe) => ({...fe, risk_classification_id: undefined}))
  }

  function handleOpenChange(next: boolean) {
    setOpen(next)
    if (!next) {
      setForm(emptyForm)
      setCiiu(null)
      setRiskId('')
      setFieldErrors({})
      setError(null)
      setCreated(null)
      setConfirmando(false)
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    const errors = validateForm(form, repIsNew, ciiu, riskId)
    setFieldErrors(errors)
    if (Object.values(errors).some(Boolean)) return
    await crear(false)
  }

  async function crear(confirmar: boolean) {
    setError(null)
    setSubmitting(true)
    try {
      const {data} = await api.post<BusinessCreated>('/businesses', {
        nit: form.nit,
        name: form.name,
        ciiu_code: ciiu?.code,
        risk_classification_id: riskId,
        legal_rep_cedula: form.legal_rep_cedula,
        legal_rep_name: repExists ? (lookup?.name ?? '') : form.legal_rep_name,
        legal_rep_last_name: repExists ? (lookup?.last_name ?? '') : form.legal_rep_last_name,
        legal_rep_email: repExists ? '' : form.legal_rep_email,
        legal_rep_phone: repExists ? null : form.legal_rep_phone || null,
        declared_worker_count: Number(form.declared_worker_count),
        billing_cycle: form.billing_cycle,
        confirmar_identidad_del_representante: confirmar,
      })
      queryClient.invalidateQueries({queryKey: ['businesses']})
      setConfirmando(false)
      setCreated(data)
    } catch (err) {
      setConfirmando(false)
      if (pideConfirmarIdentidad(err)) setConfirmando(true)
      else setError(translateApiError(err))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto" ref={setContenedor}>
        <DialogHeader>
          <DialogTitle>{created ? t('businesses.create.createdTitle') : t('businesses.create.title')}</DialogTitle>
          {created && <DialogDescription>{t('businesses.create.createdSubtitle')}</DialogDescription>}
        </DialogHeader>

        {created ? (
          <div className="space-y-4" data-testid="business-created">
            {created.legal_rep_invite_token ? (
              <>
                <p className="text-sm text-muted-foreground">{t('businesses.create.inviteSent')}</p>
                <InviteLink token={created.legal_rep_invite_token} kind="invite" />
              </>
            ) : (
              <p className="text-sm text-muted-foreground">{t('businesses.create.repHasAccount')}</p>
            )}
            <div className="flex justify-end">
              <Button
                type="button"
                data-testid="go-to-business"
                onClick={() => {
                  const id = created.id
                  handleOpenChange(false)
                  navigate(`/businesses/${id}`)
                }}>
                {t('businesses.create.goToBusiness')}
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="nit">{t('businesses.create.nit')}</Label>
              <Input
                id="nit"
                value={form.nit}
                onChange={set('nit')}
                onBlur={() => setFieldErrors((fe) => ({...fe, nit: validateNit(form.nit) ?? undefined}))}
                required
                maxLength={tope('NIT')}
                placeholder={t('businesses.create.nitPlaceholder')}
                aria-invalid={!!fieldErrors.nit}
              />
              <FieldError error={fieldErrors.nit} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">{t('businesses.create.name')}</Label>
              <Input id="name" value={form.name} onChange={set('name')} required maxLength={tope('RAZ')} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="ciiu_trigger">{t('businesses.create.ciiu')}</Label>
              <CiiuPicker ciiu={ciiu} onChange={handleCiiuChange} />
              <FieldError error={fieldErrors.ciiu_code} />
            </div>
            {ciiu && (
              <div className="space-y-2">
                <Label>{t('businesses.create.risk')}</Label>
                <RiskOptionsPicker ciiuCode={ciiu.code} riskId={riskId} onPick={handleRiskPick} />
                <FieldError error={fieldErrors.risk_classification_id} />
              </div>
            )}

            {/* Al alta el roster es solo el representante legal: sin este número toda empresa
                cotizaría en la banda más barata. */}
            <div className="space-y-2">
              <span className="flex items-center gap-1.5">
                <Label htmlFor="declared_worker_count">{t('businesses.create.workerCount')}</Label>
                {/* `contenedor` es obligatorio dentro de un diálogo: sin él Escape cierra el diálogo entero. */}
                <HelpIcon
                  label={t('common.help.iconLabelFor', {campo: t('businesses.create.workerCount')})}
                  testId="create-business-worker-count-help"
                  contenedor={contenedor}>
                  {t('businesses.create.workerCountHelp')}
                </HelpIcon>
              </span>
              <Input
                id="declared_worker_count"
                inputMode="numeric"
                value={form.declared_worker_count}
                onChange={(e) => {
                  setForm((f) => ({...f, declared_worker_count: e.target.value.replace(/\D/g, '')}))
                  setFieldErrors((fe) => ({...fe, declared_worker_count: undefined}))
                }}
                onBlur={() =>
                  setFieldErrors((fe) => ({
                    ...fe,
                    declared_worker_count: validateWorkerCount(form.declared_worker_count) ?? undefined,
                  }))
                }
                required
                maxLength={7}
                aria-invalid={!!fieldErrors.declared_worker_count}
                data-testid="create-business-worker-count"
              />
              <FieldError error={fieldErrors.declared_worker_count} />
              {/* Impresa (T15): la banda de la primera factura no se corrige después. */}
              <Hint testId="create-business-worker-count-hint">{t('businesses.create.workerCountHint')}</Hint>
            </div>

            <div className="space-y-4 border-t border-border pt-4">
              <p className="text-sm font-medium text-foreground">{t('businesses.create.legalRepSection')}</p>

              <div className="space-y-2">
                <Label htmlFor="legal_rep_cedula">{t('documento.label')}</Label>
                <div className="flex gap-2">
                  <TipoDocumentoSelect
                    value={form.legal_rep_tipo_documento}
                    onChange={(tipo) => setForm((f) => ({...f, legal_rep_tipo_documento: tipo}))}
                    testId="legal-rep-tipo-documento"
                  />
                  <Input
                    id="legal_rep_cedula"
                    className="flex-1"
                    value={form.legal_rep_cedula}
                    onChange={set('legal_rep_cedula')}
                    onBlur={() =>
                      setFieldErrors((fe) => ({
                        ...fe,
                        legal_rep_cedula:
                          validateCedula(form.legal_rep_cedula, form.legal_rep_tipo_documento) ?? undefined,
                      }))
                    }
                    required
                    maxLength={DOCUMENTO_MAX[form.legal_rep_tipo_documento]}
                    inputMode={form.legal_rep_tipo_documento === 'PA' ? 'text' : 'numeric'}
                    autoFocus
                    aria-invalid={!!fieldErrors.legal_rep_cedula}
                    data-testid="legal-rep-cedula-input"
                  />
                </div>
                <FieldError error={fieldErrors.legal_rep_cedula} />
                {cedulaValid && lookingUp && (
                  <p className="text-xs text-muted-foreground">{t('businesses.create.lookupPending')}</p>
                )}
              </div>

              {repExists && lookup && (
                <div
                  className="flex items-center gap-2 rounded-lg border border-input bg-secondary/50 px-3 py-2"
                  data-testid="legal-rep-confirm">
                  <Tag tone="neutral">{t('businesses.create.existingAccount')}</Tag>
                  <p className="text-sm text-foreground">
                    {t('businesses.create.legalRepConfirmed', {name: `${lookup.name} ${lookup.last_name}`})}
                  </p>
                </div>
              )}

              {repIsNew && (
                <div className="space-y-4" data-testid="legal-rep-new-fields">
                  <div className="space-y-2">
                    <Label htmlFor="legal_rep_name">{t('businesses.create.legalRepName')}</Label>
                    <Input
                      id="legal_rep_name"
                      value={form.legal_rep_name}
                      onChange={set('legal_rep_name')}
                      required
                      maxLength={tope('NOM')}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="legal_rep_last_name">{t('businesses.create.legalRepLastName')}</Label>
                    <Input
                      id="legal_rep_last_name"
                      value={form.legal_rep_last_name}
                      onChange={set('legal_rep_last_name')}
                      required
                      maxLength={tope('NOM')}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="legal_rep_email">{t('businesses.create.legalRepEmail')}</Label>
                    <Input
                      id="legal_rep_email"
                      inputMode="email"
                      autoComplete="email"
                      value={form.legal_rep_email}
                      onChange={set('legal_rep_email')}
                      onBlur={() =>
                        setFieldErrors((fe) => ({
                          ...fe,
                          legal_rep_email: validateEmail(form.legal_rep_email) ?? undefined,
                        }))
                      }
                      required
                      maxLength={tope('MAIL')}
                      aria-invalid={!!fieldErrors.legal_rep_email}
                    />
                    <FieldError error={fieldErrors.legal_rep_email} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="legal_rep_phone">{t('businesses.create.legalRepPhone')}</Label>
                    <Input
                      id="legal_rep_phone"
                      inputMode="tel"
                      value={form.legal_rep_phone}
                      onChange={set('legal_rep_phone')}
                      onBlur={() => {
                        const raw = form.legal_rep_phone
                        setFieldErrors((fe) => ({
                          ...fe,
                          legal_rep_phone: raw ? (validatePhone(raw) ?? undefined) : undefined,
                        }))
                      }}
                      maxLength={tope('TEL')}
                      aria-invalid={!!fieldErrors.legal_rep_phone}
                    />
                    <FieldError error={fieldErrors.legal_rep_phone} />
                  </div>
                  <p className="text-xs text-muted-foreground">{t('businesses.create.inviteNote')}</p>
                </div>
              )}
            </div>

            {/* Nace Pro con su suscripción abierta en el mismo acto: nunca un Pro sin suscripción. */}
            <div className="space-y-2 border-t border-border pt-4">
              <Label htmlFor="billing_cycle">{t('businesses.create.billingCycle')}</Label>
              <select
                id="billing_cycle"
                data-testid="create-business-billing-cycle"
                value={form.billing_cycle}
                onChange={(e) => setForm((f) => ({...f, billing_cycle: e.target.value as BillingCycle}))}
                className="h-9 w-full rounded-md border border-input bg-transparent px-2 text-sm text-foreground">
                <option value="mensual">{t('cartera.precios.mensual')}</option>
                <option value="anual">{t('cartera.precios.anual')}</option>
              </select>
              <p className="text-xs text-muted-foreground">{t('businesses.create.billingCycleHelp')}</p>
            </div>

            <FormDialogFooter
              error={error}
              pending={submitting}
              submitLabel={t('businesses.create.submit')}
              onCancel={() => handleOpenChange(false)}
            />
          </form>
        )}
      </DialogContent>
      <ConfirmarCuentaDeInciDialog
        cedula={form.legal_rep_cedula}
        open={confirmando}
        onOpenChange={setConfirmando}
        onConfirm={() => crear(true)}
        pending={submitting}
      />
    </Dialog>
  )
}
